import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, of, tap } from "rxjs";

import { NotificationService } from "../../_services/notification.service";
import { ApiKeyActions } from ".";
import { ApiKeyService } from "./api-key.service";

@Injectable()
export class ApiKeyEffects {
  private actions$ = inject(Actions);
  private apiKeyService = inject(ApiKeyService);
  private notificationService = inject(NotificationService);

  getApiKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeyActions.getApiKeys),
      mergeMap(() =>
        this.apiKeyService.getApiKeys().pipe(
          map(apiKeys => ApiKeyActions.getApiKeysSuccess({ apiKeys })),
          catchError(error =>
            of(
              ApiKeyActions.apiKeysFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  createApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeyActions.createApiKey),
      mergeMap(() =>
        this.apiKeyService.createApiKey().pipe(
          map(apiKey =>
            ApiKeyActions.createApiKeySuccess({ newApiKey: apiKey })
          ),
          catchError(error =>
            of(
              ApiKeyActions.createApiKeyFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  deleteApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeyActions.deleteApiKey),
      mergeMap(action =>
        this.apiKeyService.deleteApiKey(action.id).pipe(
          map(() => ApiKeyActions.deleteApiKeySuccess({ id: action.id })),
          catchError(error =>
            of(
              ApiKeyActions.deleteApiKeyFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  toggleApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeyActions.toggleApiKey),
      mergeMap(action => {
        // Store the target state of the API key
        // We'll use this to revert the optimistic update if there's an error
        const targetIsEnabled = action.isEnabled;

        return this.apiKeyService
          .toggleApiKey(action.id, action.isEnabled)
          .pipe(
            map(response =>
              ApiKeyActions.toggleApiKeySuccess({
                id: action.id,
                isEnabled: response,
              })
            ),
            catchError(error => {
              // Show specific error message for toggle failures
              this.notificationService.showError(
                `Failed to toggle API key: ${error.message}`
              );

              // Revert to the original state without making another API call
              return of(
                ApiKeyActions.toggleApiKeySuccess({
                  id: action.id,
                  isEnabled: !targetIsEnabled, // Revert to opposite of target state
                })
              );
            })
          );
      })
    )
  );

  deleteApiKeySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ApiKeyActions.deleteApiKeySuccess),
        tap(() =>
          this.notificationService.showSuccess("Api key deleted successfully!")
        )
      ),
    { dispatch: false }
  );

  apiKeysFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ApiKeyActions.apiKeysFailure,
          ApiKeyActions.createApiKeyFailure,
          ApiKeyActions.deleteApiKeyFailure
        ),
        map(action => {
          this.notificationService.showError(`${action.type}: ${action.error}`);
        })
      ),
    { dispatch: false }
  );

  toggleApiKeySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ApiKeyActions.toggleApiKeySuccess),
        tap(action => {
          const status = action.isEnabled ? "enabled" : "disabled";
          this.notificationService.showSuccess(
            `API key ${status} successfully!`
          );
        })
      ),
    { dispatch: false }
  );
}
